import { useEffect, useRef } from "react";

interface TrackAppLoadingParams {
  isAppReady: boolean | undefined;
  appId: string | undefined;
  appName: string | undefined;
  shouldUpdate: boolean | undefined;
  fileSizeBytes: number | undefined;
}

/**
 * Hook to track app loading performance and send metrics to parent (Fusion)
 * Sends tracking event once when app becomes ready
 */
export const useTrackAppLoading = ({
  isAppReady,
  appId,
  appName,
  shouldUpdate,
  fileSizeBytes,
}: TrackAppLoadingParams) => {
  const startTimeRef = useRef<number>(Date.now());
  const trackingSentRef = useRef<boolean>(false);

  useEffect(() => {
    if (isAppReady && !trackingSentRef.current && window.parent !== window) {
      const loadingTime = (Date.now() - startTimeRef.current) / 1000;

      // Calculate app size in MB only if we actually downloaded the file
      const appSizeMB =
        shouldUpdate === true && fileSizeBytes !== undefined
          ? fileSizeBytes / (1024 * 1024)
          : undefined;

      // Determine if app was loaded from cache
      const cached = shouldUpdate === false;

      const trackingData = {
        type: "TRACKING_EVENT",
        eventName: "Dune.AppLoad",
        properties: {
          appId,
          appName,
          loadingTime,
          cached,
          ...(appSizeMB !== undefined && { appSize: appSizeMB }),
        },
      };

      window.parent.postMessage(trackingData, "*");
      trackingSentRef.current = true;
    }
  }, [isAppReady, appId, appName, shouldUpdate, fileSizeBytes]);
};
